import type { VariantProps } from 'class-variance-authority'
import type { badgeVariants } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/dates'
import type { EmailLogEntry, EmailLogRecipient, EmailStatus } from '@/types/emailLog'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

export const emailStatusLabels: Record<EmailStatus, string> = {
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  bounced: 'Bounced',
  rejected: 'Rejected',
  complained: 'Complaint',
  received: 'Received',
  appended: 'Added to ticket',
  ticket_created: 'Ticket created',
  unmatched: 'Unmatched',
  dropped: 'Dropped',
}

// Failures red, "needs a human" amber, engagement green, plain delivery grey.
export const emailStatusVariants: Record<EmailStatus, BadgeVariant> = {
  sent: 'neutral',
  delivered: 'neutral',
  opened: 'success',
  clicked: 'success',
  bounced: 'destructive',
  rejected: 'destructive',
  complained: 'destructive',
  received: 'neutral',
  appended: 'info',
  ticket_created: 'info',
  unmatched: 'warning',
  dropped: 'neutral',
}

export const OUTBOUND_STATUSES: EmailStatus[] = ['sent', 'delivered', 'opened', 'clicked', 'bounced', 'rejected', 'complained']
export const INBOUND_STATUSES: EmailStatus[] = ['appended', 'ticket_created', 'unmatched', 'dropped', 'received']

// Inbound reasons are codes from the API's inbound pipeline; outbound reasons
// are SES's own text (bounce type, rejection message) and shown as-is.
const REASON_LABELS: Record<string, string> = {
  'unknown-sender': 'Sender is not a known user',
  'ambiguous-sender': 'Sender address matches more than one user',
  'sender-not-authenticated': 'Sender failed SPF/DKIM/DMARC checks',
  'help-desk-not-enabled': "Sender's company doesn't have the help desk",
  'unknown-ticket': "Addressed to a ticket that doesn't exist",
  'no-sender-address': 'No usable sender address',
  virus: 'Virus detected',
  spam: 'Marked as spam',
  'auto-submitted': 'Automatic reply (out of office)',
  'bulk-precedence': 'Bulk mail',
  'mailing-list': 'Mailing list traffic',
  bounce: 'Bounce notice',
  'own-domain': 'Sent from our own address',
  'empty-body': 'Empty message',
  duplicate: 'Duplicate delivery',
  'staff-reply': 'Posted as a staff reply',
  'staff-not-in-team': "Sender is an admin outside the support team, so it was posted as the reporter",
  'staff-not-authenticated': 'Sender is staff but not authenticated, so it was posted as the reporter',
  'staff-not-in-thread': "Sender is staff but didn't reply to a notification sent to them, so it was posted as the reporter",
  'ticket-closed': 'Ticket is closed',
}

export function reasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null
  return REASON_LABELS[reason] ?? reason
}

export const emailKindLabels: Record<string, string> = {
  created: 'New ticket',
  reply: 'Reply',
  assigned: 'Assigned',
  status: 'Status update',
  closed: 'Ticket closed',
}

export function kindLabel(entry: EmailLogEntry): string {
  if (entry.direction === 'inbound') return 'Inbound email'
  return (entry.kind && emailKindLabels[entry.kind]) || 'Notification'
}

// Kinds the API sends to the whole support pool. Older rows predate the
// explicit `pool` flag, so a multi-recipient email of one of these kinds is
// taken to be a pool send too.
const POOL_KINDS = new Set(['created', 'assigned', 'reply'])

/** The other party: who we sent to, or who wrote in. */
export function counterparty(entry: EmailLogEntry): string {
  if (entry.direction === 'inbound') return entry.from || '—'
  if (entry.recipientCount === 1) return entry.recipients[0]?.address || '—'
  if (entry.pool || (entry.kind && POOL_KINDS.has(entry.kind))) return `Support team (${entry.recipientCount})`
  return `${entry.recipientCount} recipients`
}

/** "8 delivered, 2 bounced" when recipients differ, else null. Most common first. */
export function statusBreakdown(entry: EmailLogEntry): string | null {
  const counts = Object.entries(entry.statusCounts ?? {}) as Array<[EmailStatus, number]>
  if (counts.length < 2) return null
  return counts
    .sort((a, b) => b[1] - a[1])
    .map(([status, n]) => `${n} ${emailStatusLabels[status].toLowerCase()}`)
    .join(', ')
}

// A click without a recorded open (pixel blocked) still means they read it.
function readIt(r: EmailLogRecipient): boolean {
  return r.openCount > 0 || r.clickCount > 0
}

export function openedByCount(entry: EmailLogEntry): number {
  return entry.openedBy ?? entry.recipients.filter(readIt).length
}

/** "Opened by 3 of 10" for a group send with readers, else null. */
export function openedByLabel(entry: EmailLogEntry): string | null {
  const n = openedByCount(entry)
  if (entry.direction !== 'outbound' || entry.recipientCount < 2 || n === 0) return null
  return `Opened by ${n} of ${entry.recipientCount}`
}

/**
 * "Opened 7th June 21:05, 2 opens, 1 click" for a recipient who read it, or
 * "Clicked a link" when only a click was recorded (the pixel was blocked).
 */
export function readSummary(r: EmailLogRecipient): string | null {
  if (!readIt(r)) return null
  const clicks = r.clickCount ? `${r.clickCount} click${r.clickCount === 1 ? '' : 's'}` : null
  if (!r.openCount) return clicks ? `Clicked a link (${clicks})` : null
  const parts = [
    r.firstOpenedAt ? `Opened ${formatDateTime(r.firstOpenedAt)}` : 'Opened',
    `${r.openCount} open${r.openCount === 1 ? '' : 's'}`,
    clicks,
  ]
  return parts.filter(Boolean).join(', ')
}

/** Readers for a hover title: "ann@x (7th June 21:05)", one per line. */
export function readersTitle(entry: EmailLogEntry): string | undefined {
  const readers = entry.recipients.filter(readIt)
  if (!readers.length) return undefined
  return readers.map(r => (r.firstOpenedAt ? `${r.address} (${formatDateTime(r.firstOpenedAt)})` : r.address)).join('\n')
}
